import React from 'react';
import { useNavigate } from 'react-router-dom';
import { hubRegions, BOOKING_LINK } from '@/data/romaniaHub';
import { MapPin, Clock, Calendar, ArrowRight, Star } from 'lucide-react';

const RegionExplorer: React.FC = () => {
  const navigate = useNavigate();

  return (
    <section id="regions" className="py-24 lg:py-32 bg-[#FAF8F3]">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
        <div className="text-center mb-20">
          <div className="flex items-center justify-center gap-4 mb-6">
            <div className="w-12 h-[1px] bg-[#C9A961]" />
            <span className="font-sans text-[10px] tracking-[0.4em] uppercase text-[#C9A961]">
              Destination Explorer
            </span>
            <div className="w-12 h-[1px] bg-[#C9A961]" />
          </div>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-[#1A3B5C] font-light mb-6">
            Romania, Region by Region
          </h2>
          <p className="font-sans text-sm text-[#666] max-w-2xl mx-auto leading-relaxed">
            Ten storied regions, each a world of its own. Explore the editorial essentials —
            the experiences, the must-see icons and the perfect length of stay.
          </p>
        </div>

        <div className="space-y-24 lg:space-y-32">
          {hubRegions.map((region, idx) => {
            const reversed = idx % 2 === 1;
            return (
              <div
                key={region.id}
                className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center"
              >
                {/* Image */}
                <div className={`relative ${reversed ? 'lg:order-2' : ''}`}>
                  <div className="relative aspect-[4/3] overflow-hidden group">
                    <img
                      src={region.image}
                      alt={region.imageAlt}
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[1200ms]"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
                  </div>
                  <div className="absolute -bottom-5 left-6 bg-[#C9A961] text-white px-6 py-3">
                    <span className="font-serif text-2xl font-light">{String(idx + 1).padStart(2, '0')}</span>
                  </div>
                </div>

                {/* Content */}
                <div className={reversed ? 'lg:order-1' : ''}>
                  <div className="flex items-center gap-2 mb-3 text-[#C9A961]">
                    <MapPin className="w-4 h-4" />
                    <span className="font-sans text-[10px] tracking-[0.3em] uppercase">Romania</span>
                  </div>
                  <h3 className="font-serif text-3xl md:text-4xl text-[#1A3B5C] font-light mb-5">
                    {region.name}
                  </h3>
                  <p className="font-sans text-sm text-[#555] leading-relaxed mb-7">
                    {region.intro}
                  </p>

                  <div className="grid sm:grid-cols-2 gap-x-8 gap-y-6 mb-8">
                    <div>
                      <p className="flex items-center gap-2 font-sans text-[10px] tracking-[0.2em] uppercase text-[#C9A961] mb-3">
                        <Star className="w-3.5 h-3.5" /> Top Experiences
                      </p>
                      <ul className="space-y-1.5">
                        {region.topExperiences.map((e) => (
                          <li key={e} className="font-sans text-xs text-[#666] leading-relaxed">— {e}</li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <p className="flex items-center gap-2 font-sans text-[10px] tracking-[0.2em] uppercase text-[#C9A961] mb-3">
                        <MapPin className="w-3.5 h-3.5" /> Must See
                      </p>
                      <ul className="space-y-1.5">
                        {region.mustSee.map((e) => (
                          <li key={e} className="font-sans text-xs text-[#666] leading-relaxed">— {e}</li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-6 mb-8 pt-6 border-t border-[#E8DFD0]">
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-[#C9A961]" />
                      <div>
                        <p className="font-sans text-[9px] tracking-wider uppercase text-[#999]">Suggested Stay</p>
                        <p className="font-sans text-xs text-[#1A3B5C]">{region.stay}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-[#C9A961]" />
                      <div>
                        <p className="font-sans text-[9px] tracking-wider uppercase text-[#999]">Best Season</p>
                        <p className="font-sans text-xs text-[#1A3B5C]">{region.season}</p>
                      </div>
                    </div>
                  </div>

                  <button
                    onClick={() => navigate(BOOKING_LINK)}
                    className="font-sans text-[11px] tracking-[0.25em] uppercase px-8 py-3.5 bg-[#1A3B5C] text-white hover:bg-[#C9A961] transition-colors duration-300 flex items-center gap-3"
                  >
                    Plan This Region <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default RegionExplorer;
